import React, {useEffect} from 'react';
import {QueryStatus} from '@reduxjs/toolkit/query';
import {type FCWithChildren} from 'types';
import {useLocalization} from 'localization';
import {usePrefetch as useCompaniesPrefetch} from 'services/companies';
import {
  isMockWorkId,
  mapWorkMetadataToProductOwnerIds,
  metadataEditLoaded,
  selectMetadataEditSavedMetadataSelector,
  useGetChangeRequestsQuery,
  useGetWorkQuery,
  useMetadataSchemas,
  useMetadataSchemasCodelists,
} from 'services/metadata';
import {useLoadError} from 'services/utils';
import {useMockContext} from 'services/utils/contexts';
import {queryStatusToLoadStatus} from 'store/api';
import {loadMockWork} from 'store/api/mock/loadMock';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import {SceneError} from 'components/scene/SceneError';
import {SceneLoader} from 'components/scene/SceneLoader';

type Props = {
  workId: string;
};

export const MetadataEditStateGate: FCWithChildren<Props> = ({
  workId,
  children,
}) => {
  const {t} = useLocalization();
  const dispatch = useAppDispatch();
  const prefetchCompanies = useCompaniesPrefetch('getCompaniesByIds');

  const metadataSchemas = useMetadataSchemas();
  const metadataCodelists = useMetadataSchemasCodelists();

  const mock = useMockContext() || isMockWorkId(workId);

  const loadedMetadata = useAppSelector(
    selectMetadataEditSavedMetadataSelector,
  );

  const {data: workMetadataData, status: workMetadataStatus} = useGetWorkQuery(
    {id: workId},
    {
      skip: mock,
    },
  );

  const eans =
    ((workMetadataData?.manifestations ?? [])
      .map(m => m.ean)
      .filter(e => e) as string[]) ?? [];

  const {data: changeRequestsData, status: changeRequestStatus} =
    useGetChangeRequestsQuery(
      {
        workId: workId,
        eans: eans,
      },
      {
        skip: mock || workMetadataStatus !== QueryStatus.fulfilled,
      },
    );

  useLoadError(
    queryStatusToLoadStatus(changeRequestStatus),
    'page.metadata.cr.load.failed',
    'warning',
  );

  useEffect(() => {
    if (workMetadataData && changeRequestsData) {
      if (
        !loadedMetadata ||
        loadedMetadata.work.id !== workMetadataData.work.id
      )
        dispatch(
          metadataEditLoaded({
            codelists: metadataCodelists,
            metadata: {
              ...workMetadataData,
              changeRequests: changeRequestsData,
            },
          }),
        );

      // Attempt to fetch all companies at once instead of fetching one at a time later.
      const productOwnerIds =
        mapWorkMetadataToProductOwnerIds(workMetadataData);
      if (productOwnerIds.length > 0) {
        prefetchCompanies({ids: productOwnerIds}, {ifOlderThan: 3600});
      }
    } else if (mock) {
      loadMockWork(workId).then(mockWork => {
        dispatch(
          metadataEditLoaded({
            codelists: metadataCodelists,
            metadata: {
              ...mockWork,
              changeRequests: [],
            },
          }),
        );
      });
    }
  }, [
    changeRequestsData,
    dispatch,
    loadedMetadata,
    metadataCodelists,
    metadataSchemas,
    mock,
    workId,
    workMetadataData,
  ]);

  if (workMetadataStatus === QueryStatus.rejected) {
    return <SceneError error={t('error.work.failedToLoad')} applySceneHeight />;
  }

  return !loadedMetadata || loadedMetadata.work.id !== workId ? (
    <SceneLoader loadingType={t('general.entity.work')} applySceneHeight />
  ) : (
    <>{children}</>
  );
};
